import React, { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';

interface KYCUser {
  id: string;
  email: string;
  walletAddress: string;
  username: string;
  createdAt: string;
}

interface PendingKYC {
  id: string;
  userId: string;
  fullName: string;
  address: string;
  phoneNumber: string;
  documentType: string;
  documentKey: string;
  documentVerificationKey: string;
  documentUrl: string;
  documentVerificationUrl: string;
  status: string;
  rejectionReason?: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  createdAt: string;
  updatedAt: string;
  user: KYCUser;
}

const AdminDashboard: React.FC = () => {
  const [pendingApplications, setPendingApplications] = useState<PendingKYC[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user, token } = useAuth();

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPendingKYC();
    }
  }, [user, token]);

  const fetchPendingKYC = async () => {
    try {
      const response = await fetch(API_URL + '/api/kyc/pending', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Pending KYC data:', data); // Debug log
        setPendingApplications(data.applications || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch pending applications');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (userId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      const response = await fetch(API_URL + `/api/kyc/status/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          rejectionReason
        })
      });

      if (response.ok) {
        setSuccess(`Application ${status} successfully`);
        // Remove the processed application from the list
        setPendingApplications(prev => prev.filter(app => app.id !== userId));
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${status} application`);
      }
    } catch (error) {
      console.error('Approval error:', error);
      setError('Network error. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Fragment>
        <div className="container-fluid py-2">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <i className="feather-alert-triangle font-xl text-warning mb-3"></i>
                  <h4 className="fw-700 text-grey-900">Access Denied</h4>
                  <p className="text-grey-500 font-xsss">You don't have permission to access this page.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid py-2">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
              <div className="card-body d-flex align-items-center p-0">
                <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">Admin Dashboard - Pending KYC Applications</h2>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger font-xsss fw-500 mb-3" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success font-xsss fw-500 mb-3" role="alert">
                {success}
              </div>
            )}

            {loading ? (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-grey-500">Loading pending applications...</p>
                </div>
              </div>
            ) : pendingApplications.length === 0 ? (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <i className="feather-check-circle font-xl text-success mb-3"></i>
                  <h4 className="fw-700 text-grey-900">All Caught Up!</h4>
                  <p className="text-grey-500 font-xsss">No pending KYC applications at the moment.</p>
                </div>
              </div>
            ) : (
              pendingApplications.map((application) => (
                <div key={application.id} className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                  <div className="card-body p-0">
                    <h4 className="fw-700 text-grey-900 font-md mb-3">{application.fullName}</h4>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="font-xsss text-grey-700 mb-1"><strong>Email:</strong> {application.user.email}</p>
                        <p className="font-xsss text-grey-700 mb-1"><strong>Username:</strong> {application.user.username || 'Not set'}</p>
                        <p className="font-xsss text-grey-700 mb-1"><strong>Phone:</strong> {application.phoneNumber}</p>
                        <p className="font-xsss text-grey-700 mb-1"><strong>Document Type:</strong> {application.documentType.replace('_', ' ').toUpperCase()}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="font-xsss text-grey-700 mb-1"><strong>Wallet:</strong> {application.user.walletAddress}</p>
                        <p className="font-xsss text-grey-700 mb-1"><strong>Applied:</strong> {formatDate(application.submittedAt)}</p>
                        <p className="font-xsss text-grey-700 mb-1"><strong>Full Name:</strong> {application.fullName}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="font-xsss text-grey-700 mb-1"><strong>Address:</strong></p>
                      <p className="font-xsss text-grey-500">{application.address}</p>
                    </div>

                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h5 className="font-xsss fw-600 text-grey-700 mb-2">Document Photo</h5>
                        <div className="border rounded p-2">
                          <img
                            src={application.documentUrl}
                            alt="Document"
                            className="w-100 rounded"
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                            onLoad={() => console.log('Document image loaded:', application.documentUrl)}
                            onError={(e) => {
                              console.error('Document image failed to load:', application.documentUrl);
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2Yzc1N2QiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                            }}
                          />
                          <a
                            href={application.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary mt-2 w-100"
                          >
                            View Full Size
                          </a>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h5 className="font-xsss fw-600 text-grey-700 mb-2">Verification Photo</h5>
                        <div className="border rounded p-2">
                          <img
                            src={application.documentVerificationUrl}
                            alt="Verification"
                            className="w-100 rounded"
                            style={{ maxHeight: '200px', objectFit: 'contain' }}
                            onLoad={() => console.log('Verification image loaded:', application.documentVerificationUrl)}
                            onError={(e) => {
                              console.error('Verification image failed to load:', application.documentVerificationUrl);
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2Yzc1N2QiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                            }}
                          />
                          <a
                            href={application.documentVerificationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary mt-2 w-100"
                          >
                            View Full Size
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success px-4 font-xsss fw-600"
                        onClick={() => handleApproval(application.id, 'approved')}
                      >
                        <i className="feather-check me-1"></i>
                        Approve
                      </button>
                      <button
                        className="btn btn-danger px-4 font-xsss fw-600"
                        onClick={() => {
                          const reason = prompt('Please provide a rejection reason:');
                          if (reason) {
                            handleApproval(application.id, 'rejected', reason);
                          }
                        }}
                      >
                        <i className="feather-x me-1"></i>
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AdminDashboard;